'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useDashboardShell } from '@/components/dashboard/dashboard-shell-context';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/dashboard/ThemeToggle';
import { NAV_GROUP_LABEL, NAV_ITEM_LABEL } from '@/lib/copy';
import type { LucideIcon } from 'lucide-react';
import {
  Map,
  Plane,
  History,
  Target,
  AlertTriangle,
  Settings,
  ShieldCheck,
  LogOut,
  X,
  MapPinned,
  Users,
  Package,
} from 'lucide-react';

type NavItem = { name: string; href: string; icon: LucideIcon };

/**
 * The rail's destinations. Every label comes from the copy module (#505 T8) and is looked up by
 * href, so a renamed label can never end up pointing at a different page than the one it names.
 */
const navGroups: { label: string; items: NavItem[] }[] = [
  {
    label: NAV_GROUP_LABEL.operations,
    items: [
      { name: NAV_ITEM_LABEL['/'], href: '/', icon: Map },
      // The catalog and the record. Operational mission work — what is flying,
      // what launches next, drawing and revising routes — lives on the air picture now.
      { name: NAV_ITEM_LABEL['/missions'], href: '/missions', icon: Target },
      { name: NAV_ITEM_LABEL['/alerts'], href: '/alerts', icon: AlertTriangle },
    ],
  },
  {
    label: NAV_GROUP_LABEL.fleet,
    items: [
      { name: NAV_ITEM_LABEL['/fleet/drones'], href: '/fleet/drones', icon: Plane },
      { name: NAV_ITEM_LABEL['/fleet/docks'], href: '/fleet/docks', icon: Package },
      { name: NAV_ITEM_LABEL['/fleet/pilots'], href: '/fleet/pilots', icon: Users },
      { name: NAV_ITEM_LABEL['/sites'], href: '/sites', icon: MapPinned },
    ],
  },
  {
    label: NAV_GROUP_LABEL.records,
    items: [
      { name: NAV_ITEM_LABEL['/flights'], href: '/flights', icon: History },
      // Past missions is NOT a row here. It is the record of one page's contents, so it is
      // reached from that page (the button on the missions board) rather than from a rail an
      // operator has to read thirteen entries of. #505 note 8.
      //
      // Compliance & Reports is NOT a row here either, for the same reason: it is a tab of
      // Settings now (`/settings?tab=compliance`). Nothing an operator does in a sortie goes
      // through it - it is NDAA status, the audit trail, maintenance records and CSV exports,
      // all of which are administration. `/compliance` redirects there (client/next.config.ts).
    ],
  },
  {
    label: NAV_GROUP_LABEL.system,
    items: [
      { name: NAV_ITEM_LABEL['/settings'], href: '/settings', icon: Settings },
      // Setup is NOT a row here. It is a one-time checklist for an org that has no sites,
      // drones, pilots or docks yet, so it does not earn a permanent rail row an operator
      // reads past every day. It is reached from Settings -> Organization, and the page
      // itself stays at `/onboarding` for anyone linked straight to it.
      { name: NAV_ITEM_LABEL['/admin'], href: '/admin', icon: ShieldCheck },
    ],
  },
];

/** Every nav href, flattened — the candidate set for resolving the active row. */
const NAV_HREFS = navGroups.flatMap((group) => group.items.map((item) => item.href));

/**
 * A nav entry matches the current path when it is that entry's route or a child of it. The COP
 * (`/`) is exact-only — as a prefix it would match every route in the app.
 */
function matchesPath(href: string, pathname: string): boolean {
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * The single href to highlight: of every entry that matches the path, the most specific (longest)
 * one wins.
 *
 * No rail entry is nested under another today - `/missions/history` was the one that was, and it
 * left the rail - so the rule currently changes no outcome. It stays because the next nested
 * destination would otherwise light two rows at once and nobody would think to look here.
 */
function resolveActiveHref(pathname: string): string | null {
  let active: string | null = null;
  for (const href of NAV_HREFS) {
    if (matchesPath(href, pathname) && (active === null || href.length > active.length)) {
      active = href;
    }
  }
  return active;
}

function NavLinks({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const pathname = usePathname();
  const activeHref = resolveActiveHref(pathname);
  const { isSuperAdmin } = useAuth();

  const groups = navGroups.map((g) =>
    g.label === NAV_GROUP_LABEL.system
      ? {
          ...g,
          items: g.items.filter((item) => item.name !== NAV_ITEM_LABEL['/admin'] || isSuperAdmin),
        }
      : g,
  );

  return (
    <nav className="flex-1 px-2 py-4 overflow-y-auto">
      {groups.map((group, gi) => (
        <div key={group.label}>
          {gi > 0 && <div className="my-3 border-t border-sidebar-border" aria-hidden />}
          {!collapsed && (
            <div className="px-3 pt-1 pb-1.5 text-[11px] font-medium uppercase tracking-wider text-sidebar-foreground/50">
              {group.label}
            </div>
          )}
          <div className={cn('space-y-0.5', !collapsed && 'pl-1.5')}>
            {group.items.map((item) => {
              const isActive = item.href === activeHref;
              return (
                <Link
                  key={`${group.label}-${item.name}`}
                  href={item.href}
                  title={collapsed ? item.name : undefined}
                  aria-label={collapsed ? item.name : undefined}
                  onClick={onNavigate}
                  className={cn(
                    'flex items-center gap-3 py-2 text-sm font-medium rounded-md transition-colors',
                    collapsed ? 'px-4 justify-center' : 'px-3',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-foreground border-l-2 border-l-sidebar-primary'
                      : 'text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent',
                  )}
                >
                  <item.icon className="size-[18px] shrink-0" strokeWidth={1.5} />
                  {!collapsed && item.name}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

function UserBlock({
  collapsed,
  beforeLogout,
}: {
  collapsed: boolean;
  /** e.g. close mobile drawer before sign-out */
  beforeLogout?: () => void;
}) {
  const { user, role, isSuperAdmin, logout } = useAuth();

  const initials =
    user?.full_name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '??';

  return (
    <div className={cn('py-4 border-t border-sidebar-border', collapsed ? 'px-2' : 'px-4')}>
      <div className={cn('flex items-center', collapsed ? 'flex-col gap-2' : 'gap-3')}>
        {/* Collapsed, this disc is the rail's whole answer to "who am I signed
            in as, and what am I allowed to do" - and off the COP that is now the
            permanent state, so it needs the hover name the rest of the rail's
            collapsed controls already have. The role belongs in it because the
            role is what explains a control being missing on /settings. */}
        <div
          title={
            collapsed
              ? `${user?.full_name ?? 'Signed in'} (${isSuperAdmin ? 'SUPERADMIN' : role})`
              : undefined
          }
          className="flex items-center justify-center size-7 rounded-full bg-sidebar-accent text-[11px] font-medium text-sidebar-foreground shrink-0"
        >
          {initials}
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-sidebar-foreground truncate">{user?.full_name}</p>
            <span
              className={`inline-flex items-center px-1.5 py-0.5 text-[11px] font-medium tracking-wider uppercase rounded ${isSuperAdmin ? 'bg-accent-violet/20 text-accent-violet-fg' : 'bg-sidebar-primary/20 text-sidebar-primary'}`}
            >
              {isSuperAdmin ? 'SUPERADMIN' : role}
            </span>
          </div>
        )}
      </div>
      <ThemeToggle collapsed={collapsed} />
      {/* Named the same way the nav links and ThemeToggle are, and for a
          sharper reason: lucide auto-applies aria-hidden to a childless icon,
          so collapsed this button's accessible name was genuinely EMPTY and it
          was the one control in the rail with no tooltip either. It is also the
          only destructive one — an operator who has learned "hover tells me
          what this does" gets nothing here and finds out by clicking, which
          signs them out mid-flight. Collapsed is now the default for everyone,
          so this is on every load rather than one breakpoint. */}
      <button
        type="button"
        onClick={() => {
          beforeLogout?.();
          void logout();
        }}
        title={collapsed ? 'Log out' : undefined}
        aria-label={collapsed ? 'Log out' : undefined}
        className={cn(
          'mt-1 flex items-center w-full py-2 text-sm font-medium text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-md transition-colors',
          collapsed ? 'px-0 justify-center' : 'px-3',
        )}
      >
        <LogOut className="size-4" strokeWidth={1.5} />
        {!collapsed && <span className="ml-2">Log out</span>}
      </button>
    </div>
  );
}

/**
 * How long a pointer has to stay on the rail before it opens, in ms.
 *
 * Without it the rail opens on every pass of a pointer travelling to the strip
 * board, which is the most-used surface on the COP and sits directly beside it.
 *
 * 300 rather than the 120 this shipped with first. The expanded rail covers the
 * left 140 px of the strip board, and its nav links land in the same vertical
 * band as the aircraft rows - so a rail opened by accident turns a click meant
 * for a callsign into a navigation off the air picture. 120 ms is cleared by any
 * momentary pause; 300 needs a deliberate dwell.
 */
const HOVER_INTENT_MS = 300;

/** Ties the header press path's `aria-controls` to the region it opens. */
const RAIL_REGION_ID = 'dashboard-navigation-rail';

/** Names the landmark, which was an unlabelled `<aside>` over a live map. */
const NAV_LANDMARK_LABEL = 'Main navigation';

/**
 * The logo and the wordmark, drawn identically whether the header carrying them
 * is inert (in flow) or is the press path that opens the rail (floating). One
 * component rather than two copies, because the two headers differ only in the
 * element they sit in and a divergence between them would be invisible until
 * someone compared the COP against every other route.
 */
function RailHeaderMark({ showLabels }: { showLabels: boolean }) {
  return (
    <>
      {/* `isolate` so the glow stays inside this wrapper's own stacking context.
          Without it the `-z-10` layer paints in the aside's negative layer,
          UNDER the header's own background - and the header now has an opaque
          `hover:bg-sidebar-accent`, so the brand glow disappeared exactly while
          the pointer was on the control it marks. */}
      <div className="relative isolate shrink-0">
        <Logo className="size-7" />
        <div className="absolute inset-0 blur-md bg-sidebar-primary/30 -z-10" />
      </div>
      {showLabels && (
        <span className="text-lg font-semibold tracking-tight text-sidebar-foreground truncate">
          SHOVRON
        </span>
      )}
    </>
  );
}

export interface SidebarProps {
  /**
   * Take the rail OUT of the layout's flex row and draw it over the page.
   *
   * Set on the COP. `frozen` used to be set there instead, and its reasoning is
   * the whole argument for this: the sidebar was in the same flex row as the map,
   * so an animating width was a `map.resize()` per animation frame
   * (`DroneMap.tsx:781`) - the aircraft the operator was tracking sliding
   * sideways for the length of the transition. T6 answered that by deleting the
   * expansion. Floating removes the cause instead, so the expansion comes back:
   * an overlay can animate its width as much as it likes and the canvas never
   * changes size.
   *
   * The width saved is modest and worth stating honestly - 64 px reserved
   * becomes 48 px covered, so 16 px of hard gain. What it actually buys is that
   * the canvas is continuous from edge to edge, the resize is gone, and the rail
   * can be expanded again.
   *
   * The rail must publish what it covers before it floats (D9). It does that
   * through `DashboardShellProvider`, because it is rendered above every route
   * and cannot reach the COP's own inset registry.
   *
   * Below `lg` the drawer form is untouched.
   */
  floating?: boolean;
}

export function Sidebar({ floating = false }: SidebarProps) {
  const { breakpointMode, drawerOpen, setDrawerOpen, setRailInsetPx, manualFlightEngaged } =
    useDashboardShell();
  // Collapsed by default: the icon rail is the resting state, so the operator
  // keeps the width for the map. Only the FLOATING rail can leave it, by a press
  // on its header, and that choice resets on reload.
  const [userCollapsed, setUserCollapsed] = useState(true);
  // Transient, and separate from the toggle above: a hover opens the rail for as
  // long as the pointer stays, and closes it again on the way out without
  // changing what the operator chose with the button.
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pathname = usePathname();

  // Floating rails expand at every width `lg` and up, `icons` included: the
  // reason `icons` never expanded was that there was no room for the labels
  // beside the map, and an overlay does not take any.
  //
  // In flow there is no expansion at all, by hover or by press (D1). The rail is
  // the icon strip and stays that way: the chevron was asked for by nobody and
  // removed on request, and a sidebar that can still be swept open by a passing
  // pointer is the same moving surface under another name. Nothing is lost but
  // the visible label text - every nav row keeps its `title` and `aria-label`
  // while collapsed, so the destinations stay named to a tooltip and to a screen
  // reader.
  const floatingExpandable = floating && breakpointMode !== 'drawer';
  const floatingOpen = floatingExpandable && (!userCollapsed || hoverExpanded);

  const showLabels = floatingOpen;
  const collapsed = !showLabels && breakpointMode !== 'drawer';

  const clearHoverTimer = () => {
    if (hoverTimer.current !== null) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  const close = useCallback(() => {
    clearHoverTimer();
    setHoverExpanded(false);
    setUserCollapsed(true);
  }, []);

  // Closes on navigation - but ONLY when floating. Off the COP the rail is a
  // labelled sidebar an operator opens deliberately, it survives route changes
  // within the dashboard group, and collapsing it on every click would be a
  // regression on every route that is not the air picture.
  useEffect(() => {
    if (!floating) return;
    close();
  }, [pathname, floating, close]);

  // A breakpoint change must not carry an open rail across. Crossing to the
  // drawer with the rail expanded and back again would otherwise return with
  // `userCollapsed` still false - and browser zoom crosses `lg` on a 1280 px
  // window, so this is reachable without touching the window.
  useEffect(() => {
    if (floatingExpandable) return;
    close();
  }, [floatingExpandable, close]);

  // An aircraft came into the operator's hand while the rail was open. The two
  // guards below stop it OPENING from here on; this is the case they cannot
  // cover, because the rail was already over the strip board before the request
  // was made. It gets out of the way rather than waiting to be dismissed.
  //
  // Only on the transition, so the toggle still works during a manual session:
  // this effect does not re-run while `manualFlightEngaged` stays true.
  useEffect(() => {
    if (!manualFlightEngaged) return;
    close();
  }, [manualFlightEngaged, close]);

  useEffect(() => () => clearHoverTimer(), []);

  /**
   * Dismissal from anywhere, which is what makes the expansion momentary.
   *
   * Without it a rail opened by the toggle has no way to close on a touch
   * screen: `pointerleave` never fires without a pointer, so it sits at 196 px
   * over the left of the picture until someone finds the chevron again. This is
   * plan acceptance item 4, "collapses on a map press".
   *
   * Capture phase and no `preventDefault`, so the press still reaches whatever
   * it was aimed at - closing the rail must not also swallow the click.
   * `keydown` on the window rather than the rail, because Escape has to work
   * once focus has moved back to the map.
   */
  useEffect(() => {
    if (!floatingOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (railRef.current?.contains(event.target as Node)) return;
      close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [floatingOpen, close]);

  /**
   * What the rail permanently covers, measured off a box that never animates.
   *
   * The first cut measured the `<aside>` itself and skipped the write while
   * `showLabels` was true. That guard is only half a guard, and both halves bite:
   *
   * - On COLLAPSE, `showLabels` is already false when the effect re-runs, so it
   *   sampled the box at the START of the 260 ms width transition - 196 px - and
   *   the fresh `ResizeObserver` then published a new value every frame all the
   *   way down to 48. The strip board, the map key, the airspace panel and the
   *   top bar all slid 148 px on every hover-out, which is exactly the dancing
   *   this inset exists to prevent.
   * - Coming back from the drawer breakpoint with the rail still open, the
   *   effect re-entered with `showLabels` true and published NOTHING, leaving the
   *   inset stuck at 0 while a 196 px opaque rail sat over the stage. Everything
   *   left-anchored then draws underneath it, including the flight control
   *   surface's grab halo.
   *
   * A sentinel fixed at the resting width has neither problem: there is no
   * transition to sample and no state in which measuring is wrong, so the guard
   * goes away rather than being made cleverer.
   */
  const railRef = useRef<HTMLElement | null>(null);
  const restingRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (!floating || breakpointMode === 'drawer') {
      setRailInsetPx(0);
      return;
    }
    const el = restingRef.current;
    if (!el) return;

    const measure = () => {
      // The strip alone, because nothing overflows the aside any more.
      //
      // This was the union of the strip and the chevron: that button was an
      // absolutely positioned child hanging PAST the border box, so measuring
      // the box understated what the rail covered, and the surfaces that stepped
      // aside landed under a control that then swallowed their clicks. The press
      // path is the rail's own header now, which is inside the box, so the union
      // has one operand and the COP gets the overhang back as map (D4).
      //
      // Up, never down: a rounding error must cost a pixel of map rather than
      // put the strip board a pixel under the rail.
      setRailInsetPx(Math.ceil(el.getBoundingClientRect().right));
    };
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [floating, breakpointMode, setRailInsetPx]);

  if (breakpointMode === 'drawer') {
    if (!drawerOpen) return null;

    return (
      <>
        <div
          role="presentation"
          className="fixed inset-0 z-40 bg-black/70 lg:hidden"
          onClick={() => setDrawerOpen(false)}
        />
        <aside className="fixed inset-y-0 left-0 z-50 flex w-56 flex-col bg-sidebar border-r border-sidebar-border shadow-xl lg:hidden">
          <div className="flex items-center justify-between gap-2 p-4 border-b border-sidebar-border">
            <div className="flex items-center gap-2 min-w-0">
              <Logo className="size-7 shrink-0" />
              <span className="text-lg font-semibold tracking-tight text-sidebar-foreground truncate">SHOVRON</span>
            </div>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="p-2 rounded-md text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent"
              aria-label="Close navigation"
            >
              <X className="size-5" strokeWidth={1.5} />
            </button>
          </div>
          <NavLinks collapsed={false} onNavigate={() => setDrawerOpen(false)} />
          <UserBlock collapsed={false} beforeLogout={() => setDrawerOpen(false)} />
        </aside>
      </>
    );
  }

  return (
    <aside
      ref={railRef}
      id={floating ? RAIL_REGION_ID : undefined}
      aria-label={NAV_LANDMARK_LABEL}
      data-testid="dashboard-rail"
      data-floating={floating ? 'true' : 'false'}
      data-expanded={showLabels ? 'true' : 'false'}
      // Hover with an intent delay, because the strip board - the most-used
      // surface on the COP - sits directly beside this, and a pointer travelling
      // to it must not drag the rail open on the way past.
      onPointerEnter={(event) => {
        if (!floatingExpandable || event.pointerType !== 'mouse') return;
        // Never while an aircraft is in the operator's hand. Expanded, this rail
        // covers the strip board and puts its own nav links in the same vertical
        // band as the aircraft rows - and a link press unmounts the COP, which
        // RELEASES manual authority on the wire. `manualExitGuard` confirms the
        // three selection clicks that end a session and cannot see a navigation,
        // so the rail declines to open rather than the guard learning to route.
        // Hover needs no click at all, which is what makes it the hazard; the
        // toggle is untouched.
        if (manualFlightEngaged) return;
        // Not while a button is held. MapLibre pans on mouse events and never
        // takes pointer capture, so a drag westward across the map crosses this
        // rail with the button down - and a 196 px panel opening over the map
        // being panned, then staying open because the pointer ended inside it,
        // is the worst version of this whole affordance.
        if (event.buttons !== 0) return;
        clearHoverTimer();
        hoverTimer.current = setTimeout(() => setHoverExpanded(true), HOVER_INTENT_MS);
      }}
      onPointerLeave={() => {
        clearHoverTimer();
        setHoverExpanded(false);
      }}
      // Keyboard gets the labels too: tabbing into a rail of unlabelled icons is
      // the same problem the expansion exists to solve.
      //
      // Except from the toggle itself. `focus` fires before `click`, so a tap on
      // the toggle would expand the rail here and then the click handler - which
      // by then reads `showLabels` as true - would collapse it straight back.
      // That is the one path a touch screen has, so it would have been dead on
      // exactly the device the button exists for.
      onFocus={(event) => {
        if (!floatingExpandable) return;
        // Same refusal as the hover above, and for the same reason rather than
        // for the keyboard's: what is dangerous is the rail being OPEN over the
        // rows, whoever opened it - a mouse click aimed at a callsign lands on a
        // nav link either way. Collapsed, every link keeps its `title` and its
        // accessible name, so nothing is lost here but the visible labels.
        if (manualFlightEngaged) return;
        if ((event.target as Element | null)?.closest?.('[data-rail-toggle]')) return;
        setHoverExpanded(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setHoverExpanded(false);
        }
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || !floatingOpen) return;
        clearHoverTimer();
        setHoverExpanded(false);
        setUserCollapsed(true);
      }}
      className={cn(
        'flex flex-col h-screen border-r border-sidebar-border',
        floating
          // Out of the flex row entirely. This is the whole change: an overlay's
          // width can animate as much as it likes and the map canvas beneath it
          // never resizes, so the aircraft the operator is tracking stays put.
          //
          // `surface-rail` rather than a hand-rolled alpha, so `globals.css`
          // turns it solid under `prefers-reduced-transparency` and
          // `prefers-contrast: more`. Its own class rather than
          // `surface-chrome-heavy`, because that one paints a `--popover` ground
          // and everything in this rail is coloured against `--sidebar`: the
          // active and hover row fills are `bg-sidebar-accent`, which on popover
          // is a ~1.09:1 step and effectively invisible.
          //
          // `z-40`, above the bottom stack's `z-30`. They tie otherwise, and the
          // rail is rendered first, so the command bar won and painted over the
          // rail's theme toggle and Log out - unclickable, with a flight key on
          // top of them.
          ? 'absolute inset-y-0 left-0 z-40 surface-rail'
          : 'relative shrink-0 bg-sidebar',
        // A width transition is only safe once nothing relayouts with it. In
        // flow it is a `map.resize()` per frame on the COP, which is why T6
        // froze the rail rather than animate it.
        // A 148 px surface sweeping across live moving map content is the
        // textbook vestibular case, and the state change stays perceivable
        // without the slide - the labels simply appear.
        floating && 'transition-[width] duration-[var(--duration-sheet)] ease-[var(--ease-drawer)] motion-reduce:transition-none',
        // Floating: 48 px at rest, 196 px open. In flow: 64 px, and it never
        // changes, so there is no width to transition (D1).
        //
        // Deliberately not JS constants: the width that matters to anything else
        // is the one the rail MEASURES and publishes, and a second copy in
        // TypeScript would be a number that can disagree with the class that
        // actually draws the box.
        floating ? (showLabels ? 'w-[196px]' : 'w-12') : 'w-16',
      )}
    >
      {/* The press path, and the only one there is on a touch screen. An RC
          screen and a tablet have no hover at all, and expansion must not be
          bolted onto pressing a nav icon - that would make every navigation two
          presses.

          The rail's own header carries it rather than a control of its own (D2).
          It is already at the top of the resting strip, so it adds no chrome
          over the map; it is inside the aside's border box, so unlike the
          chevron it does not widen what the rail covers; and it cannot travel
          out from under the operator's hand as the rail opens, which is what the
          chevron did until it was anchored to the strip.

          `data-rail-toggle` moves across with the behaviour, and so does the
          focus exemption above (D3): `focus` fires before `click`, so without it
          a tap expands the rail on focus and this handler - reading `showLabels`
          as true by then - collapses it straight back. Dead on exactly the
          device the press path exists for.

          In flow the header is inert, because there is no expansion to reach
          (D1).

          Two things about the naming (D5). Open, the button carries the visible
          word SHOVRON, so its accessible name must contain that word or a
          speech-input operator saying "click SHOVRON" hits nothing - WCAG 2.5.3
          Label in Name, level A, and newly in scope because that wordmark used
          to sit on an inert `div`. Collapsed there is no visible text at all,
          only the mark, so the criterion does not apply and the name stays the
          bare action. And it carries a `title` either way, because every other
          control in this rail names itself on hover while collapsed - the nav
          rows, the theme toggle, Log out - and the one that opens the rail was
          the only one that did not. */}
      {floatingExpandable ? (
        <button
          type="button"
          data-rail-toggle
          onClick={() => {
            // Refused while an aircraft is in the operator's hand, for the same
            // reason hover and focus are - and a reason this control did not
            // have until now. The press path used to be a 32 px chevron sitting
            // inboard, where "a deliberate press was never the hazard" held. It
            // is now a 48 x 68 px target in the literal top-left corner of the
            // screen, which is where a hand holding a tablet rests. Expanded,
            // this rail covers the strip board and puts its nav links in the
            // same vertical band as the aircraft rows, and a link press unmounts
            // the COP, which RELEASES manual authority on the wire.
            //
            // The effect above already shuts the rail when manual flight
            // engages; without this, one accidental press reopens it (D7).
            if (manualFlightEngaged) return;
            clearHoverTimer();
            setHoverExpanded(false);
            setUserCollapsed(showLabels);
          }}
          className={cn(
            'flex w-full cursor-pointer items-center gap-2 py-5 border-b border-sidebar-border text-left transition-colors hover:bg-sidebar-accent active:bg-sidebar-border',
            collapsed ? 'px-4 justify-center' : 'px-4',
          )}
          aria-label={showLabels ? 'SHOVRON, collapse sidebar' : 'Expand sidebar'}
          // A refusal an operator can read, rather than a press that does
          // nothing. Not `disabled`, which would drop the tooltip that carries
          // the explanation and dim the brand mark.
          title={
            manualFlightEngaged
              ? 'Menu unavailable while flying manually'
              : showLabels
                ? 'Collapse sidebar'
                : 'Expand sidebar'
          }
          aria-disabled={manualFlightEngaged}
          aria-expanded={showLabels}
          aria-controls={RAIL_REGION_ID}
        >
          <RailHeaderMark showLabels={showLabels} />
        </button>
      ) : (
        /* In flow the header opens nothing, because there is nothing to open
           (D1) - so it goes home instead of being a dead pixel-identical twin of
           the control the operator just learned on the COP (D8). Pressing the
           same mark in the same corner and getting no movement, no highlight and
           no cursor change reads as broken, not as by design.

           `/` is the air picture, so this is also a one-press route back to it
           from anywhere in the dashboard, which #505 note 5 wanted on its own.
           It never renders on the COP itself: that route is the floating form,
           where this header is the expand control. */
        <Link
          href="/"
          aria-label="SHOVRON, go to the air picture"
          title="SHOVRON, go to the air picture"
          className={cn(
            'flex items-center gap-2 py-5 border-b border-sidebar-border transition-colors hover:bg-sidebar-accent',
            collapsed ? 'px-4 justify-center' : 'px-4',
          )}
        >
          <RailHeaderMark showLabels={showLabels} />
        </Link>
      )}

      {/* The strip the rail always occupies, as an element rather than as a
          number. Inert and invisible: it exists to be measured, and it is the
          one box here that never animates. `w-12` mirrors the aside's own
          collapsed width above - the same strip described twice, once to draw
          and once to measure. */}
      {floating && (
        <span
          ref={restingRef}
          aria-hidden
          data-testid="dashboard-rail-resting"
          className="pointer-events-none absolute inset-y-0 left-0 w-12"
        />
      )}

      <NavLinks collapsed={collapsed} />

      <UserBlock collapsed={collapsed} />
    </aside>
  );
}
